import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DeviceTable from './DeviceTable';
import FirmwareUpdateModal from './FirmwareUpdateModal';
import DeviceAlertModal from './DeviceAlertModal';
import { Device } from "../types";
import { getLoggingModeErrorMessage, updateDeviceLoggingMode } from '../utils/loggingMode';
import { fieldsForOtaMode, OtaMode } from '../utils/otaMode';

interface MyDevicesProps {
  apiBaseUrl: string;
  canInstallFirmware?: boolean;
  readOnly?: boolean;
  showOwnerColumns?: boolean;
}

const MyDevices: React.FC<MyDevicesProps> = ({
  apiBaseUrl,
  canInstallFirmware = true,
  readOnly = false,
  showOwnerColumns = true,
}) => {
  const [myDevices, setMyDevices] = useState<Device[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [updatingOTA, setUpdatingOTA] = useState<string | null>(null);
  const [firmwareUpdateDevice, setFirmwareUpdateDevice] = useState<Device | null>(null);
  const [alertDevice, setAlertDevice] = useState<Device | null>(null);

  const fetchMyDevices = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${apiBaseUrl}/devices/my-devices`, {
        withCredentials: true
      });
      setMyDevices(response.data.devices);
      setError(null);
    } catch (error: any) {
      console.error('Error fetching my devices:', error);
      setError('Failed to fetch my devices');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMyDevices();
  }, []);

  const handleToggleMarked = async (serial: string, value: OtaMode) => {
    try {
      setUpdatingOTA(serial);
      
      // Update the server
      await axios.patch(
        `${apiBaseUrl}/devices/${serial}`,
        { deployment: { otaMode: value } },
        { withCredentials: true }
      );
      
      // Then update local state
      setMyDevices((prev) =>
        prev.map((device) =>
          device.serial === serial
            ? { ...device, deployment: { ...device.deployment, ...fieldsForOtaMode(value) } }
            : device
        )
      );
      
      setError(null);
    } catch (error: any) {
      console.error('Error updating device OTA status:', error);
      setError(`Failed to update OTA status: ${error.response?.data?.message || error.message}`);
      
      // Revert local state in case of failure
      fetchMyDevices();
    } finally {
      setUpdatingOTA(null);
    }
  };

  const handleUpdateLoggingMode = async (serial: string, mode: number) => {
    try {
      const { device } = await updateDeviceLoggingMode(apiBaseUrl, serial, mode);

      setMyDevices((prev) =>
        prev.map((entry) =>
          entry.serial === serial ? { ...entry, ...device, loggingMode: mode } : entry
        )
      );

      setError(null);
    } catch (error: unknown) {
      console.error('Error updating device logging mode:', error);
      setError(`Failed to update logging mode: ${getLoggingModeErrorMessage(error)}`);
      fetchMyDevices();
    }
  };

  return (
    <div className="mb-5">
      <DeviceTable
        devices={myDevices}
        loading={loading}
        error={error}
        apiBaseUrl={apiBaseUrl}
        onToggleMarked={handleToggleMarked}
        onUpdateLoggingMode={handleUpdateLoggingMode}
        onFirmwareInstall={canInstallFirmware ? setFirmwareUpdateDevice : undefined}
        onAlertsOpen={setAlertDevice}
        updatingOTA={updatingOTA}
        showManagementControls={!readOnly}
        showOwnerColumns={showOwnerColumns}
      />
      <FirmwareUpdateModal
        apiBaseUrl={apiBaseUrl}
        device={firmwareUpdateDevice}
        isOpen={firmwareUpdateDevice !== null}
        onClose={() => setFirmwareUpdateDevice(null)}
      />
      <DeviceAlertModal
        apiBaseUrl={apiBaseUrl}
        device={alertDevice}
        isOpen={alertDevice !== null}
        onClose={() => setAlertDevice(null)}
      />
    </div>
  );
};

export default MyDevices;